/**
 * Client-list helpers shared by the Clients overview and the brand switcher:
 * search, sort, health filtering and parent/child-aware brand search.
 *
 * @module lib/clientHealth
 */

import type { ClientBrand, ClientBrandOverview, ClientHealth } from "../api/clientBrands";

export type ClientSortKey = "attention" | "name" | "followers" | "scheduled" | "last_published";

export const CLIENT_SORTS: { key: ClientSortKey; label: string }[] = [
  { key: "attention", label: "Needs attention" },
  { key: "name", label: "Name (A–Z)" },
  { key: "followers", label: "Most followers" },
  { key: "scheduled", label: "Fewest scheduled" },
  { key: "last_published", label: "Least recently published" },
];

export type HealthFilter = "all" | ClientHealth;

const HEALTH_RANK: Record<ClientHealth, number> = { critical: 0, attention: 1, healthy: 2 };

export function healthOf(o: ClientBrandOverview): ClientHealth {
  return o.health ?? "healthy";
}

function byName(a: ClientBrandOverview, b: ClientBrandOverview): number {
  return a.brand.name.localeCompare(b.brand.name, undefined, { sensitivity: "base" });
}

function ts(v: string | null | undefined): number {
  return v ? new Date(v).getTime() : 0;
}

export function sortOverviews(list: ClientBrandOverview[], key: ClientSortKey): ClientBrandOverview[] {
  const out = [...list];
  switch (key) {
    case "attention":
      return out.sort(
        (a, b) =>
          HEALTH_RANK[healthOf(a)] - HEALTH_RANK[healthOf(b)] ||
          (b.attention_count ?? 0) - (a.attention_count ?? 0) ||
          byName(a, b)
      );
    case "followers":
      return out.sort((a, b) => b.total_followers - a.total_followers || byName(a, b));
    case "scheduled":
      return out.sort(
        (a, b) =>
          (a.posts_scheduled_next_14d ?? a.posts_scheduled_next_7d) -
            (b.posts_scheduled_next_14d ?? b.posts_scheduled_next_7d) || byName(a, b)
      );
    case "last_published":
      return out.sort((a, b) => ts(a.last_published_at) - ts(b.last_published_at) || byName(a, b));
    default:
      return out.sort(byName);
  }
}

export function normalize(s: string): string {
  return s.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "");
}

export function filterOverviews(
  list: ClientBrandOverview[],
  query: string,
  health: HealthFilter,
  parentNameFor: (id: string | null) => string | null = () => null
): ClientBrandOverview[] {
  const q = normalize(query.trim());
  return list.filter((o) => {
    if (health !== "all" && healthOf(o) !== health) return false;
    if (!q) return true;
    const hay = normalize(`${o.brand.name} ${o.brand.slug} ${parentNameFor(o.brand.parent_brand_id) ?? ""}`);
    return hay.includes(q);
  });
}

export function healthCounts(list: ClientBrandOverview[]): Record<ClientHealth, number> {
  const counts: Record<ClientHealth, number> = { critical: 0, attention: 0, healthy: 0 };
  for (const o of list) counts[healthOf(o)] += 1;
  return counts;
}

// ── Brand switcher search ──────────────────────────────────────────────────────

export interface BrandOption {
  id: string | null;
  name: string;
  depth: 0 | 1;
  parentName: string | null;
  color: string | null;
}

/**
 * Flatten brands into switcher options: parents A–Z, each followed by its
 * children A–Z. With a query, a matching child still shows (with its parent
 * named as context) and a matching parent keeps its children visible.
 */
export function brandOptions(brands: ClientBrand[], query: string): BrandOption[] {
  const q = normalize(query.trim());
  const byNameAsc = (a: ClientBrand, b: ClientBrand) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  const ids = new Set(brands.map((b) => b.id));
  // A child whose parent is missing/archived is treated as top-level.
  const parents = brands.filter((b) => !b.parent_brand_id || !ids.has(b.parent_brand_id)).sort(byNameAsc);
  const children = (pid: string) => brands.filter((b) => b.parent_brand_id === pid).sort(byNameAsc);
  const match = (b: ClientBrand) => !q || normalize(`${b.name} ${b.slug}`).includes(q);

  const out: BrandOption[] = [];
  for (const p of parents) {
    const kids = children(p.id);
    const parentHit = match(p);
    const kidHits = parentHit ? kids : kids.filter(match);
    if (!parentHit && kidHits.length === 0) continue;
    out.push({ id: p.id, name: p.name, depth: 0, parentName: null, color: p.primary_color });
    for (const k of kidHits) {
      out.push({ id: k.id, name: k.name, depth: 1, parentName: p.name, color: k.primary_color });
    }
  }
  return out;
}
